import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShoppingCart, X, Plus, Minus, Send, Search } from 'lucide-react';
import axios from 'axios';
import { downloadPdf } from '../utils/downloadPdf';
import { products, brands, Product } from '../data/products';
import { getCategoryIcon, getBrandLogo, getBrandGradient } from '../utils/productAssets';

const API = process.env.REACT_APP_BACKEND_URL;

interface CartItem { product: Product; quantity: number; }

export default function LicensesPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [productImages, setProductImages] = useState<Record<string, string>>({});

  useEffect(() => {
    axios.get(`${API}/api/products/images`).then(r => setProductImages(r.data || {})).catch(() => {});
  }, []);

  const filtered = products.filter(p => {
    const matchBrand = selectedBrand === 'All' || p.brand === selectedBrand;
    const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchBrand && matchSearch;
  });

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) return prev.map(item => item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => item.product.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  const [lastQuoteId, setLastQuoteId] = useState('');

  const removeFromCart = (id: string) => setCart(prev => prev.filter(item => item.product.id !== id));

  const submitQuote = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const details = cart.map(item => `${item.product.brand} - ${item.product.name} (${item.product.category}) x${item.quantity}`).join('\n');
      const { data } = await axios.post(`${API}/api/quotes`, {
        product_category: 'licenses',
        product_name: cart.map(i => i.product.name).join(', ').substring(0, 200),
        quantity: cart.reduce((sum, i) => sum + i.quantity, 0),
        details,
        contact_email: contactEmail || user?.email || '',
        contact_phone: contactPhone,
        company_name: companyName || user?.company || '',
      });
      setLastQuoteId(data._id);
      setQuoteSubmitted(true);
      setCart([]);
    } catch {} finally { setSubmitting(false); }
  };

  const totalItems = cart.reduce((sum, i) => sum + i.quantity, 0);

  // Get categories for current brand filter
  const filteredCategories = Array.from(new Set(filtered.map(p => p.category)));

  return (
    <>
      <Header />
      <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #0A1128 0%, #0D47A1 100%)', padding: '56px 24px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--secondary-light, #EF5350)', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 12 }}>MALUZ TECHNOLOGIES</p>
          <h1 data-testid="licenses-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 700, color: 'white', marginBottom: 12, letterSpacing: '-0.02em' }}>
            {t('mp.title')}
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', maxWidth: 500, margin: '0 auto' }}>
            Microsoft, Adobe, Kaspersky, 3CX, Ekahau — {filtered.length} {t('mp.products_available')}
          </p>
        </div>

        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 24px' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {brands.map(b => (
                <button key={b} data-testid={`brand-filter-${b.toLowerCase()}`} onClick={() => setSelectedBrand(b)}
                  style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: selectedBrand === b ? '#0A1128' : 'white', color: selectedBrand === b ? 'white' : 'var(--text-secondary)', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', transition: 'all 0.2s' }}>
                  {b}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input data-testid="product-search" placeholder={t('mp.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--border)', fontSize: 13, width: 200, outline: 'none', background: 'white' }} />
              </div>
              <button data-testid="cart-toggle-btn" onClick={() => setCartOpen(!cartOpen)}
                style={{ position: 'relative', padding: '8px 16px', background: 'var(--secondary)', color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <ShoppingCart size={16} />
                {totalItems > 0 && (
                  <span style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, background: '#0A1128', color: 'white', borderRadius: '50%', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{totalItems}</span>
                )}
                Cart
              </button>
            </div>
          </div>

          {/* Products grouped by category */}
          {filteredCategories.map(category => (
            <div key={category} style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid var(--border)' }}>
                {category}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
                {filtered.filter(p => p.category === category).map(product => {
                  const CategoryIcon = getCategoryIcon(product.category);
                  const brandLogo = getBrandLogo(product.brand);
                  const brandGradient = getBrandGradient(product.brand);
                  const customImage = productImages[product.id];
                  return (
                  <div key={product.id} data-testid={`product-card-${product.id}`}
                    style={{ background: 'white', border: '1px solid var(--border)', overflow: 'hidden', transition: 'transform 0.18s ease, box-shadow 0.18s ease', borderRadius: 4 }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.10)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    {/* Visual hero area */}
                    <div style={{ height: 140, background: customImage ? '#FFFFFF' : brandGradient, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {customImage ? (
                        <img src={customImage} alt={product.name} loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                      ) : (
                        <>
                          {/* Decorative pattern */}
                          <div style={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '14px 14px' }} />
                          {/* Brand logo fallback */}
                          {brandLogo && (
                            <img src={brandLogo} alt={`${product.brand} logo`} loading="lazy"
                              style={{ width: 56, height: 56, objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))', position: 'relative', zIndex: 2 }} />
                          )}
                          {/* Category icon decoration (top-right) */}
                          <div style={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.45)', zIndex: 1 }}>
                            <CategoryIcon size={20} strokeWidth={1.5} />
                          </div>
                        </>
                      )}
                      {/* Brand strip bottom */}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: product.brandColor }} />
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '2px 6px', background: `${product.brandColor}12`, color: product.brandColor, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{product.brand}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CategoryIcon size={11} strokeWidth={2} /> {product.category}
                        </span>
                      </div>
                      <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10, lineHeight: 1.3, minHeight: 36 }}>{product.name}</h3>
                      <button data-testid={`add-to-cart-${product.id}`} onClick={() => addToCart(product)}
                        style={{ width: '100%', padding: '8px', background: '#0A1128', color: 'white', border: 'none', fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'background 0.2s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = product.brandColor)}
                        onMouseLeave={e => (e.currentTarget.style.background = '#0A1128')}>
                        <Plus size={12} /> {t('mp.add_to_quote')}
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Cart Sidebar */}
        {cartOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setCartOpen(false)} />
            <div data-testid="cart-sidebar" style={{ position: 'relative', width: '100%', maxWidth: 420, background: 'white', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700 }}>{t('mp.quote_cart')} ({totalItems})</h2>
                <button onClick={() => setCartOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
                {quoteSubmitted ? (
                  <div style={{ textAlign: 'center', padding: 32 }}>
                    <div style={{ fontSize: 40, marginBottom: 12, color: 'var(--primary)' }}>&#10003;</div>
                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t('quote.success')}</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>We will contact you shortly.</p>
                    {lastQuoteId && (
                      <button onClick={() => downloadPdf(`/api/quotes/${lastQuoteId}/pdf`, `quote-${lastQuoteId.slice(-8)}.pdf`)} data-testid="download-quote-pdf"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: 13, border: 'none', marginBottom: 12, textTransform: 'uppercase', cursor: 'pointer' }}>
                        {t('mp.download_pdf')}
                      </button>
                    )}
                    <br />
                    <button onClick={() => { setQuoteSubmitted(false); setCartOpen(false); }} style={{ padding: '10px 20px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{t('mp.continue_shopping')}</button>
                  </div>
                ) : cart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    <ShoppingCart size={36} style={{ marginBottom: 10, opacity: 0.3 }} />
                    <p style={{ fontSize: 14 }}>{t('mp.empty_cart')}</p>
                  </div>
                ) : (
                  <>
                    {cart.map(item => (
                      <div key={item.product.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bg-section)' }}>
                        <div style={{ width: 4, background: item.product.brandColor, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 10, color: item.product.brandColor, fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{item.product.brand}</p>
                          <p style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{item.product.name}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                            <button onClick={() => updateQuantity(item.product.id, -1)} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontSize: 12 }}><Minus size={10} /></button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, minWidth: 16, textAlign: 'center' }}>{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product.id, 1)} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontSize: 12 }}><Plus size={10} /></button>
                          </div>
                        </div>
                        <button onClick={() => removeFromCart(item.product.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', alignSelf: 'flex-start', padding: 2 }}><X size={14} /></button>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input data-testid="cart-email" placeholder={`${t('mp.contact_email')} *`} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required
                        style={{ padding: '9px 10px', border: '1px solid var(--border)', fontSize: 13, outline: 'none' }} />
                      <input data-testid="cart-phone" placeholder="Phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
                        style={{ padding: '9px 10px', border: '1px solid var(--border)', fontSize: 13, outline: 'none' }} />
                      <input data-testid="cart-company" placeholder="Company Name" value={companyName} onChange={e => setCompanyName(e.target.value)}
                        style={{ padding: '9px 10px', border: '1px solid var(--border)', fontSize: 13, outline: 'none' }} />
                    </div>
                  </>
                )}
              </div>

              {!quoteSubmitted && cart.length > 0 && (
                <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
                  <button data-testid="submit-quote-btn" onClick={submitQuote} disabled={submitting || !contactEmail}
                    style={{ width: '100%', padding: '12px', background: 'var(--secondary)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: submitting || !contactEmail ? 0.5 : 1 }}>
                    <Send size={14} /> {t('mp.request_quote')} ({totalItems} {t('mp.items')})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
